import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardPen, LayoutGrid, Package, Wallet, SlidersHorizontal, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { scMessage } from "./scUtils";
import { CountSheet } from "./CountSheet";
import { Board } from "./Board";
import { ItemsAdmin } from "./ItemsAdmin";
import { CostsAdmin } from "./CostsAdmin";
import { SetupAdmin } from "./SetupAdmin";

const C = OPS.sc;

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

// Only built tabs exist: an unknown tab key from the server is never rendered.
// `located` tabs need a location in scope; the finance admin tabs do not, so a
// fresh install with no location yet still reaches items / costs / setup.
const TAB_DEFS = {
    count: { icon: ClipboardPen, label: "stk_tab_count", located: true,
             render: (s) => <CountSheet scope={s} /> },
    board: { icon: LayoutGrid, label: "stk_tab_board", located: true,
             render: (s) => <Board scope={s} /> },
    items: { icon: Package, label: "stk_tab_items", located: false,
             render: (s) => <ItemsAdmin scope={s} /> },
    costs: { icon: Wallet, label: "stk_tab_costs", located: false,
             render: (s) => <CostsAdmin scope={s} /> },
    setup: { icon: SlidersHorizontal, label: "stk_tab_setup", located: false,
             render: (s, reload) => <SetupAdmin scope={s} onChanged={reload} /> },
};

/** The server's message for `state: "no_location"` (`cause` is the
 *  `no_scope_error` detail), else the generic note. */
function noLocationText(scope, lang, t) {
    const d = scope?.cause && typeof scope.cause === "object" ? scope.cause : null;
    const msg = d ? (lang === "ar" ? d.message_ar || d.message_en : d.message_en || d.message_ar) : null;
    return msg || t("stk_no_location");
}

/**
 * «الجرد الشهري» inside the branch hub. The tab LIST is the server's
 * (`section.tabs` from `/branch-ops/scope`); locations, caps, periods and
 * labels come from ONE `GET /sc/scope`, passed down as `scope`. An empty
 * location scope is a note with the server's own message, never a form.
 */
export function StockCountSection({ section }) {
    const { t, lang, dir } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        api.get("/sc/scope")
            .then((r) => { if (alive) setScope(r.data); })
            .catch((e) => { if (alive) setError(e); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [attempt]);

    // Setup edits (locations, settings) change the scope: refetch it in place,
    // without the loader, so the open tab keeps its state.
    const reloadScope = useCallback(async () => {
        try {
            const r = await api.get("/sc/scope");
            setScope(r.data);
            setError(null);
        } catch {
            /* keep the scope we have; the admin screen reports its own errors */
        }
    }, []);

    const tabs = (section?.tabs || []).filter((k) => TAB_DEFS[k]);
    const requested = searchParams.get("tab");
    const active = tabs.includes(requested) ? requested
        : (tabs.includes(section?.default_tab) ? section.default_tab : tabs[0]);

    const onTabChange = (val) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    if (loading) return <InlineLoader label={t("stk_loading")} />;

    let note = null;
    // no response at all (weak signal): the note offers a retry instead of a page reload
    const offline = !!error && !scMessage(error, lang);
    if (error) note = scMessage(error, lang) || t("stk_not_ready");
    else if (scope?.state !== "ok") note = noLocationText(scope, lang, t);

    const canRender = (k) => !error && !!scope && (scope.state === "ok" || !TAB_DEFS[k].located);

    return (
        <div data-testid={C.section} data-state={scope?.state || "error"} className="space-y-5">
            {note ? (
                <div data-testid={C.note}
                     className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                    <p className="min-w-0 flex-1 text-sm font-semibold text-[color:var(--ink-muted)]">{note}</p>
                    {offline ? (
                        <Button size="sm" variant="outline" data-testid={C.retry} className="shrink-0"
                                onClick={() => setAttempt((n) => n + 1)}>
                            {t("stk_retry")}
                        </Button>
                    ) : null}
                </div>
            ) : null}
            {/* Radix Tabs stamps dir="ltr" on its root unless told otherwise (there is no
                DirectionProvider), which would lay every stock-count screen out left-to-right. */}
            {tabs.length && !error ? (
                <Tabs value={active} onValueChange={onTabChange} dir={dir} className="space-y-5">
                    <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-[1.75rem] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto sm:rounded-full">
                        {tabs.map((k) => {
                            const Icon = TAB_DEFS[k].icon;
                            return (
                                <TabsTrigger key={k} data-testid={C.tab(k)} value={k} className={triggerClass}>
                                    <Icon className="h-4 w-4" />
                                    {t(TAB_DEFS[k].label)}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                    {tabs.map((k) => (
                        <TabsContent key={k} value={k} className="mt-0 focus-visible:outline-none">
                            {canRender(k) ? TAB_DEFS[k].render(scope, reloadScope) : null}
                        </TabsContent>
                    ))}
                </Tabs>
            ) : null}
        </div>
    );
}
